import { useCallback, useEffect, useState } from 'react';
import { controlCentrePeriodRange } from '../utils/controlCentrePeriodRange';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { API_ENDPOINTS } from '../config/api';
import { addUtcDays } from '../utils/ordersDailyPresentation';
import { londonDayWindow } from '../utils/controlCentreCards';
import { MAINTENANCE_SCHEDULE_HISTORY_DAYS } from '../utils/maintenanceScheduleWindows';
import type { AquariusData } from '../types/fleetDataHub';
import type { MaintenanceSummaryData } from '../components/fleetDataHub/MaintenanceSection';
import type { MaintenanceScheduleData } from '../components/fleetDataHub/MaintenanceScheduleView';
import type { WalkaroundResponseLike } from '../utils/controlCentreCards';

/**
 * Re-read today's figures while the Control Centre is the page on screen.
 * drivers/summary runs several Athena scans per call and the server caches
 * for only 45 s, so this is deliberately slow.
 */
const REFRESH_MS = 15 * 60_000;

export interface TodayRead<T> {
  data: T | null;
  loading: boolean;
  /** The read failed; the figure stays Unknown. */
  failed: boolean;
}

export interface ControlCentreToday {
  walkaround: TodayRead<WalkaroundResponseLike>;
  drivers: TodayRead<AquariusData>;
  maintenance: TodayRead<MaintenanceSummaryData>;
  schedule: TodayRead<MaintenanceScheduleData>;
  reload: () => void;
}

const idle = <T,>(): TodayRead<T> => ({ data: null, loading: false, failed: false });
const pending = <T,>(): TodayRead<T> => ({ data: null, loading: true, failed: false });

async function getJson<T>(url: string): Promise<T> {
  const res = await authenticatedFetch(url);
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json() as Promise<T>;
}

/**
 * The reads the Drivers, Compliance and Maintenance cards need, for one UK day.
 * These are the same four endpoints the Driver and Maintenance tabs already call
 * (drivers/check-compliance, drivers/summary, maintenance/summary,
 * maintenance/schedule), with the same window shape; no new endpoint. Each read
 * fails on its own, and a failed read leaves that card Unknown.
 *
 * `enabled` is false for demo and whenever the Control Centre is not the page
 * on screen (the app keeps visited pages mounted but hidden): no reading and
 * no polling then, and the last figures stay put. Becoming enabled reads at once.
 *
 * Telematics and Orders reuse what the hub and useOrdersDailyProjection load.
 */
export function useControlCentreToday(enabled: boolean, londonToday: string): ControlCentreToday {
  const [state, setState] = useState<Omit<ControlCentreToday, 'reload'>>({
    walkaround: enabled ? pending() : idle(),
    drivers: enabled ? pending() : idle(),
    maintenance: enabled ? pending() : idle(),
    schedule: enabled ? pending() : idle(),
  });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    // Nothing held yet reads as loading, not Unknown, while the first read is in flight.
    setState((s) => ({
      walkaround: s.walkaround.data ? s.walkaround : pending(),
      drivers: s.drivers.data ? s.drivers : pending(),
      maintenance: s.maintenance.data ? s.maintenance : pending(),
      schedule: s.schedule.data ? s.schedule : pending(),
    }));
    // The UK day, as real instants (not 00:00Z to 23:59Z, which is an hour off in summer).
    const { from, to } = londonDayWindow(londonToday);
    // History is only for on-time completions; whole days are enough.
    const scheduleFrom = `${addUtcDays(londonToday, -(MAINTENANCE_SCHEDULE_HISTORY_DAYS - 1))}T00:00:00.000Z`;
    const settle = <K extends keyof Omit<ControlCentreToday, 'reload'>>(
      key: K,
      p: Promise<NonNullable<ControlCentreToday[K]['data']>>,
    ) => {
      p.then(
        (data) => { if (!cancelled) setState((s) => ({ ...s, [key]: { data, loading: false, failed: false } })); },
        () => {
          // Keep the last good figures on screen across a failed background poll.
          if (!cancelled) setState((s) => ({ ...s, [key]: { data: s[key].data, loading: false, failed: true } }));
        },
      );
    };
    settle('walkaround', getJson(API_ENDPOINTS.fleetIntelligence.driversCheckCompliance({ from, to })));
    // Drivers' hours are yesterday's: tacho data is about a day behind, so today is never complete.
    // The Driver tab's stored Yesterday window, so it is served from storage.
    const hoursDay = controlCentrePeriodRange('yesterday');
    settle('drivers', getJson(API_ENDPOINTS.fleetIntelligence.driversSummary({
      from: hoursDay.start.toISOString(),
      to: hoursDay.end.toISOString(),
    })));
    settle('maintenance', getJson(API_ENDPOINTS.fleetIntelligence.maintenanceSummary({ from, to })));
    settle('schedule', getJson(API_ENDPOINTS.fleetIntelligence.maintenanceSchedule({ from: scheduleFrom, to })));
    return () => { cancelled = true; };
  }, [enabled, londonToday, tick]);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') reload();
    }, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [enabled, reload]);

  return { ...state, reload };
}
